import { ChevronDownIcon, EditIcon, ExternalLinkIcon } from '@assets/icons';
import { IconButton } from '@components/button';
import { Group } from '@components/layout';
import { MarkdownDisplay } from '@components/markdown';
import { Typography } from '@components/typography';
import { Collapse, Stack } from '@mui/material';
import { useToggle } from 'react-use';
import { ServerTag } from '../../ServerTag';
import { ResourceCard } from '../ResourceCard';
import { UserResourceWarning } from '../UserResourceWarning';

export type RulesCardProps = {
  rules: string;
  source: 'platform' | 'organization' | 'workspace' | 'user' | 'service';
  startOpen?: boolean;
  title?: string;
  showUserRulesWarning?: boolean;
  onEdit?: () => void;
  onNavigate?: () => void;
  editDisabled?: string;
  flex?: boolean;
};

export const RulesCard = ({
  rules,
  source,
  startOpen = false,
  title,
  showUserRulesWarning = false,
  onEdit,
  onNavigate,
  editDisabled,
  flex = false,
}: RulesCardProps) => {
  const [open, toggleOpen] = useToggle(startOpen);

  const handleEdit = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.stopPropagation();
    onEdit?.();
  };

  const handleNavigate = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.stopPropagation();
    onNavigate?.();
  };

  return (
    <ResourceCard onClick={toggleOpen} flex={flex && open}>
      <Stack
        sx={{
          width: '100%',
          overflow: 'hidden',
          ...(flex && open ? { flex: 1, minHeight: 0 } : {}),
        }}
      >
        <Group alignItems="center" justifyContent="space-between">
          <Group alignItems="center" gap={2}>
            <Typography variant="body1">
              {title || <span style={{ textTransform: 'capitalize' }}>{source}</span>} rules
            </Typography>
            <ServerTag
              label={source}
              color={source}
              variant="caption2"
              sx={{ textTransform: 'capitalize' }}
            />
          </Group>
          <Group>
            {onEdit && (
              <IconButton
                Icon={EditIcon}
                onClick={handleEdit}
                disabled={Boolean(editDisabled)}
                tooltip={editDisabled}
              />
            )}
            {onNavigate && <IconButton Icon={ExternalLinkIcon} onClick={handleNavigate} />}
            <IconButton
              Icon={ChevronDownIcon}
              iconProps={{ style: { transform: open ? 'rotate(180deg)' : 'rotate(0deg)' } }}
            />
          </Group>
        </Group>
        <Collapse
          in={open}
          sx={{
            overflow: 'hidden',
            ...(flex && open
              ? {
                  flex: 1,
                  minHeight: 0,
                  '& .MuiCollapse-wrapper': { height: '100%' },
                  '& .MuiCollapse-wrapperInner': {
                    height: '100%',
                    display: 'flex',
                    flexDirection: 'column',
                  },
                }
              : {}),
          }}
        >
          {showUserRulesWarning && <UserResourceWarning resource="rules" sx={{ marginTop: 3 }} />}
          {rules !== undefined &&
            rules !== null &&
            (rules.length > 0 ? (
              <MarkdownDisplay
                source={rules}
                style={{ padding: 4 }}
                containerSx={{
                  marginTop: 3,
                  overflow: 'auto',
                  ...(flex ? { flex: 1, minHeight: 0 } : { maxHeight: '600px' }),
                }}
              />
            ) : (
              <Typography
                variant="body2"
                color="foreground.paperCard.light"
                sx={{ marginTop: 3, fontStyle: 'italic' }}
              >
                No {source} rules configured.
              </Typography>
            ))}
        </Collapse>
      </Stack>
    </ResourceCard>
  );
};
